import { useMemo } from "react";

import { Slider as SliderPrimitive } from "@base-ui/react/slider";

import { cn } from "@lib/utils";

/**An input where the user selects a value from within a given range.
 * @prop className - The class name to apply to the slider
 * @prop defaultValue - The default value of the slider
 * @prop value - The value of the slider
 * @prop min - The minimum value of the slider
 * @prop max - The maximum value of the slider
 */
export const Slider = ({
	className,
	defaultValue,
	value,
	min = 0,
	max = 100,
	...props
}: SliderPrimitive.Root.Props) => {
	const _values = useMemo(
		() => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
		[value, defaultValue, min, max],
	);

	return (
		<SliderPrimitive.Root
			className={cn("data-horizontal:w-full data-vertical:h-full", className)}
			data-slot="slider"
			defaultValue={defaultValue}
			value={value}
			min={min}
			max={max}
			thumbAlignment="edge"
			{...props}
		>
			<SliderPrimitive.Control
				className={`relative flex w-full touch-none items-center select-none data-disabled:opacity-50
					data-vertical:h-full data-vertical:min-h-40 data-vertical:w-auto data-vertical:flex-col`}
			>
				<SliderPrimitive.Track
					data-slot="slider-track"
					className={`relative h-1 grow overflow-hidden rounded-sm bg-block select-none data-horizontal:h-1
						data-horizontal:w-full data-vertical:h-full data-vertical:w-1`}
				>
					<SliderPrimitive.Indicator
						data-slot="slider-range"
						className="h-1 rounded-sm bg-white select-none data-horizontal:h-full data-vertical:w-full"
					/>
				</SliderPrimitive.Track>
				{Array.from({ length: _values.length }, (_, index) => (
					<SliderPrimitive.Thumb
						data-slot="slider-thumb"
						key={index}
						className={`border-ring ring-ring/50 relative block size-3 shrink-0 cursor-pointer rounded-2xl border-2
						border-white bg-secondary transition-[color,box-shadow] select-none after:absolute after:-inset-2
						focus-visible:ring-3 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50`}
					/>
				))}
			</SliderPrimitive.Control>
		</SliderPrimitive.Root>
	);
};

Slider.displayName = "Slider";
